/**
 * « Contrat » tab of the vehicle (fiche 22811): the shared `EntityInfoCard`
 * bound to `VEHICLE_CONTRACT_FIELDS` + the « Voir le contrat » action
 * (stable key `viewContract`, `actions` bucket: `setActionLabel` /
 * `hideAction`) opening `contractPath` — the contract detail of the
 * vehicle's `ID_NPOL`, built by the page.
 *
 * The action only shows with a record: on the empty / error states the card
 * displays nothing but the message (« afficher uniquement … »).
 *
 * Registered as `extranet.vehicleContractCard` (`./register.ts`).
 */

import { useMemo } from 'react';

import type { LayoutOverrides } from '@components/shared/configurable';

import { EntityInfoCard } from '../shared/EntityInfoCard';
import type { EntityInfoCardDataProps } from '../shared/schemas';
import { FLEET_COMPONENT_IDS } from './fleetComponentIds';
import { VEHICLE_CONTRACT_FIELDS, viewContractAction } from './vehicleInfoConfig';

export type VehicleContractCardProps = EntityInfoCardDataProps & {
  /** Absolute path of the contract detail; omitted ⇒ no « Voir le contrat ». */
  contractPath?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
};

export function VehicleContractCard({
  values,
  loading = false,
  error = null,
  contractPath,
  _layoutOverrides,
}: Readonly<VehicleContractCardProps>) {
  const hasRecord = !loading && !error && values !== null;
  const actions = useMemo(
    () => (hasRecord && contractPath ? [viewContractAction(contractPath)] : undefined),
    [hasRecord, contractPath],
  );

  return (
    <EntityInfoCard
      componentId={FLEET_COMPONENT_IDS.vehicleContract}
      fields={VEHICLE_CONTRACT_FIELDS}
      values={values}
      loading={loading}
      error={error}
      actions={actions}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
